/**
 * Subscription ID Utilities
 * 
 * Handles parsing and creation of composite subscription IDs.
 * Format: `{type}-{id}` (e.g., "membership-123", "pass-456")
 * 
 * Used for:
 * - URL routing (/subscriptions/confirmed/membership-123)
 * - Context state management
 * - API request routing
 * 
 * @module lib/utils
 */

/**
 * Subscription type discriminator
 */
export type SubscriptionType = 'membership' | 'pass';

const PLAN_TYPES = ['membership', 'pass'] as const;

/**
 * What a CONFIRMATION url can point at. `gift` is not a plan — it is a receipt
 * for an order the buyer kept nothing from, so it is keyed by the INVOICE they
 * paid rather than by a pass they own.
 */
export type ConfirmationTarget = SubscriptionType | 'gift';

const CONFIRMATION_TARGETS = ['membership', 'pass', 'gift'] as const;

/**
 * Parsed subscription ID components
 */
export interface ParsedSubscriptionId {
  type: SubscriptionType;
  id: number;
  compositeId: string;
}

/** As above, but the id may be an invoice id when `type` is `gift`. */
export interface ParsedConfirmationId {
  type: ConfirmationTarget;
  id: number;
  compositeId: string;
}

/**
 * Create a composite subscription ID
 * 
 * @param type - Subscription type (membership or pass)
 * @param id - Numeric ID
 * @returns Composite ID string (e.g., "membership-123")
 * 
 * @example
 * ```typescript
 * const compositeId = createCompositeId('membership', 123);
 * // Returns: "membership-123"
 * ```
 */
export function createCompositeId(type: ConfirmationTarget, id: number): string {
  return `${type}-${id}`;
}

/**
 * Parse a composite subscription ID
 * 
 * @param compositeId - Composite ID string (e.g., "membership-123")
 * @returns Parsed subscription ID components or null if invalid
 * 
 * @example
 * ```typescript
 * const parsed = parseCompositeId('membership-123');
 * // Returns: { type: 'membership', id: 123, compositeId: 'membership-123' }
 * 
 * const invalid = parseCompositeId('invalid-format');
 * // Returns: null
 * ```
 */
export function parseCompositeId(compositeId: string): ParsedSubscriptionId | null {
  return parseAs(compositeId, PLAN_TYPES);
}

/**
 * Parse a composite id from a CONFIRMATION url, which may also be a gift
 * receipt (`gift-<invoiceId>`).
 *
 * Deliberately a second function rather than widening `parseCompositeId`: that
 * one also reads the `?plan=` deep link, where `usePreselectedPlan` branches
 * `type === 'membership' ? memberships : passes` — a widened union would make a
 * crafted `?plan=gift-5` resolve silently as a pass. Plans and receipts are
 * different vocabularies; only receipts know about gifts.
 */
export function parseConfirmationId(compositeId: string): ParsedConfirmationId | null {
  return parseAs(compositeId, CONFIRMATION_TARGETS);
}

/** The shared shape rule: exactly `<type>-<positive int>`. */
function parseAs<T extends string>(
  compositeId: string,
  allowed: readonly T[],
): { type: T; id: number; compositeId: string } | null {
  const parts = compositeId.split('-');

  if (parts.length !== 2) {
    return null;
  }

  const [type, idStr] = parts;

  if (!allowed.includes(type as T)) {
    return null;
  }

  const id = parseInt(idStr, 10);

  if (isNaN(id) || id <= 0) {
    return null;
  }

  return {
    type: type as T,
    id,
    compositeId,
  };
}

/**
 * Validate a composite subscription ID format
 * 
 * @param compositeId - Composite ID string to validate
 * @returns True if valid format, false otherwise
 * 
 * @example
 * ```typescript
 * isValidCompositeId('membership-123'); // true
 * isValidCompositeId('pass-456');       // true
 * isValidCompositeId('invalid');        // false
 * isValidCompositeId('membership-abc'); // false
 * ```
 */
export function isValidCompositeId(compositeId: string): boolean {
  return parseCompositeId(compositeId) !== null;
}
